// Wallpaper: Einstellungen, Vorschau, Start und Stopp des Renderers.
import { h, type VNode } from 'snabbdom';
import { api, type GameDetail, type GameRow, type LessonPly, type WallpaperMode, type WallpaperSettings, type WallpaperStatus } from '../api';
import { appPieces, debounce, names } from '../util';
import { previewView } from './wppreview';
import { boardsOf, tempo } from '../wpmath';
import { check, range, select } from './wpfields';
import { getLang, locale, t } from '../i18n';

/** Modus, Name und Erklärung; die Texte laufen über t() */
const MODES: [WallpaperMode, string, string][] = [
  ['single', 'Einzel', 'Ein Brett, Größe und Lage frei'],
  ['gallery', 'Galerie', 'Drei Partien nebeneinander, sie ziehen versetzt'],
  ['simul', 'Simultan', 'Viele Bretter, das Brett mit dem letzten Zug leuchtet auf'],
  ['sheet', 'Partiezettel', 'Brett und mitgeschriebene Notation'],
  ['trail', 'Lichtspur', 'Die letzten zehn Züge als verglimmende Lichtbahnen'],
  ['poster', 'Plakat', 'Spieler groß gesetzt, Zugnummer im Hintergrund'],
  ['learn', 'Lernen', 'Eröffnung, Theorie und Stockfish-Bewertung; bei Fehlern hält die Partie an'],
];

export class WallpaperCtrl {
  settings: WallpaperSettings | null = null;
  status: WallpaperStatus | null = null;
  games: GameRow[] = [];
  error = '';
  saved = false;
  busy = false;
  // Vorschau: spielt die ersten ausgewählten Partien ab
  previews: GameDetail[] = [];
  previewPly = 0;
  /** Takte seit dem Laden, für die Modi mit mehreren Brettern */
  ticks = 0;
  /** Lernmodus: Lerndaten der ersten Partie und Fortschritt der Hintergrundrechnung */
  lesson: LessonPly[] = [];
  progress: { done: number; total: number } | null = null;
  private timer: number | undefined;

  constructor(readonly redraw: () => void) {}

  async load() {
    try {
      const w = await api.wallpaper();
      this.settings = w.settings;
      this.status = w.status;
      this.games = w.games;
      this.error = '';
      const ids = w.games.slice(0, 4).map(g => g.id);
      if (ids.join() !== this.previews.map(p => p.game.id).join()) {
        this.previews = await Promise.all(ids.map(id => api.game(id)));
        this.previewPly = 0;
      }
      await this.loadLesson();
      // Texte im Wallpaper folgen der Sprache der Oberfläche
      if (this.settings.lang !== getLang()) this.persist();
      this.schedule();
    } catch (e) {
      this.error = t('Wallpaper-Einstellungen nicht geladen: {e}', { e: (e as Error).message });
    }
    this.redraw();
  }

  private async loadLesson() {
    if (this.settings?.mode !== 'learn' || !this.previews[0]) return;
    [this.lesson, this.progress] = await Promise.all([api.lesson(this.previews[0].game.id), api.lessons()]);
  }

  private schedule() {
    clearTimeout(this.timer);
    const s = this.settings, first = this.previews[0];
    if (!first || !s) return;
    const last = first.plies.length - 1, n = boardsOf(s);
    // Lernmodus: nach einem Zug mit Urteil drei Takte stehen bleiben, wie im Renderer
    const hold = s.mode === 'learn' && this.lesson[this.previewPly]?.judgement ? 3 : 1;
    const wait = s.mode !== 'single' ? (s.secondsPerMove * tempo(s, n) * hold) / n
      : this.previewPly >= last - 1 ? Math.max(s.secondsPerMove, s.endPauseSeconds) : s.secondsPerMove;
    this.timer = window.setTimeout(() => {
      if (!location.hash.startsWith('#/wallpaper')) return;
      this.ticks++;
      // neue Bewertungen aus der Hintergrundrechnung abholen
      if (this.ticks % 20 === 0 && this.progress && this.progress.done < this.progress.total) void this.loadLesson();
      this.previewPly = this.previewPly >= last ? 0 : this.previewPly + 1;
      this.redraw();
      this.schedule();
    }, wait * 1000);
  }

  private persist = debounce(async () => {
    if (!this.settings) return;
    try {
      // Figurensatz wird im Setup für alle Module gewählt; hier nur mitschicken, nie überschreiben
      this.settings = await api.saveWallpaper({ ...this.settings, pieces: appPieces(), lang: getLang() });
      this.saved = true;
      this.error = '';
      setTimeout(() => ((this.saved = false), this.redraw()), 1500);
    } catch (e) {
      this.error = t('Nicht gespeichert: {e}', { e: (e as Error).message });
    }
    this.redraw();
  }, 300);

  set<K extends keyof WallpaperSettings>(k: K, v: WallpaperSettings[K]) {
    if (!this.settings) return;
    this.settings = { ...this.settings, [k]: v };
    if (k === 'secondsPerMove' || k === 'endPauseSeconds' || k === 'mode' || k === 'boards') this.schedule();
    if (k === 'mode') void this.loadLesson().then(() => this.redraw());
    this.redraw();
    this.persist();
  }

  async startStop() {
    this.busy = true;
    this.redraw();
    try {
      this.status = this.status?.running ? await api.stop() : await api.start();
      this.error = '';
    } catch (e) {
      this.error = (e as Error).message;
    }
    this.busy = false;
    this.redraw();
  }

  async remove(g: GameRow) {
    await api.setWallpaper(g.id, false);
    await this.load();
  }
}

export function wallpaperView(c: WallpaperCtrl): VNode {
  const s = c.settings;
  if (!s) return c.error ? h('p.error', c.error) : h('p.muted', 'lade …');
  const st = c.status;
  return h('section.wallpaper', [
    h('div.wp-main', [
      previewView(s, c.previews, c.previewPly, c.ticks, c.lesson),
      h('div.wp-status', [
        h('span', [t('Renderer') + ' ', h('b', st?.running ? t('läuft (PID {p})', { p: st.pid ?? '' }) : t('aus'))]),
        h('span', [t('Partien') + ' ', h('b', String(c.games.length))]),
        h('button.btn.accent', { attrs: { disabled: c.busy || (!st?.running && !c.games.length) }, on: { click: () => c.startStop() } },
          st?.running ? t('Wallpaper beenden') : t('Wallpaper starten')),
        c.saved ? h('span.ok', t('gespeichert')) : null,
      ]),
      s.mode === 'learn' && c.progress ? h('p.muted.small', [
        t('Bewertet: {d} von {n} Partien', { d: c.progress.done, n: c.progress.total }), ' · ',
        t('Stockfish rechnet im Hintergrund mit niedriger Priorität, ohne Internet.'),
      ]) : null,
      st && !st.rendererFound ? h('p.warn', t('Renderer nicht gefunden unter {p}. Bauen mit: cd renderer && cargo build --release', { p: st.renderer })) : null,
      c.error ? h('p.error', c.error) : null,
      h('h2', t('Playlist')),
      c.games.length
        ? h('ol.playlist', c.games.map(g => h('li', { key: g.id }, [
            h('a', { attrs: { href: `#/partie/${g.id}` } }, names(g)),
            g.year ? h('span.muted', ` ${g.year}`) : null,
            h('button.link', { on: { click: () => c.remove(g) } }, t('entfernen')),
          ])))
        : h('p.muted', t('Noch leer. In der Bibliothek das Häkchen „Wallpaper“ setzen.')),
    ]),
    h('aside.setup', [
      h('div.hd', h('h3', t('Einstellungen'))),
      range(c, 'secondsPerMove', t('Zugtempo'), 0.5, 30, 0.5, v => `${v.toLocaleString(locale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`),
      range(c, 'endPauseSeconds', t('Pause nach Partieende'), 0, 60, 1, v => `${v} s`),
      h('div.fld', [
        select(c, 'mode', t('Modus'), MODES.map(([m, l]) => [m, t(l)])),
        h('p.muted.small', t(MODES.find(([m]) => m === s.mode)![2])),
      ]),
      s.mode === 'simul' ? range(c, 'boards', t('Bretter'), 2, 64, 1, v => String(v)) : null,
      range(c, 'boardSize', t('Brettgröße'), 0.1, 0.85, 0.01, v => `${Math.round(v * 100)} %`,
        t(s.mode === 'single' ? 'Anteil der Bildschirmhöhe' : 'Anteil der Bildschirmhöhe; passt es nicht, wird verkleinert')),
      h('div.fld', [
        select(c, 'position', t('Position'), [['center', t('Mitte')], ['left', t('links')], ['right', t('rechts')]]),
        select(c, 'theme', t('Brett'), [['night', t('Nacht')], ['brown', t('lichess braun')], ['blue', t('lichess blau')]]),
        select(c, 'animation', t('Zuganimation'), [['none', t('keine')], ['fast', t('schnell')], ['normal', t('normal')], ['slow', t('langsam')]]),
        select(c, 'afterGame', t('Danach'), [['next', t('nächste Partie')], ['repeat', t('wiederholen')], ['random', t('zufällig')]]),
      ]),
      range(c, 'brightness', t('Helligkeit'), 0.3, 1, 0.05, v => `${Math.round(v * 100)} %`),
      h('div.fld', [
        check(c, 'showNames', t('Spielernamen')),
        check(c, 'showYear', t('Jahr (wenn vorhanden)')),
        check(c, 'showMove', t('aktueller Zug in Notation')),
        check(c, 'showLastMove', t('letzten Zug markieren')),
        check(c, 'showCoordinates', t('Koordinaten')),
      ]),
      h('div.fld', [
        h('span.lbl', t('Sparsam')),
        check(c, 'pauseOnBattery', t('pausieren im Akkubetrieb')),
        h('p.muted.small', t('Außerdem pausiert der Renderer, wenn der Desktop verdeckt ist oder das Display schläft. Unter Linux fehlt das noch.')),
      ]),
    ]),
  ]);
}
